import type { AggregatedNoteDto } from "../../services/vorgangNoteService";
import type { NoteRefDto } from "../../services/vorgangService";

/**
 * Das Blockmodell der Notizfläche (PO 08.09.2026) — REINE Ableitung, bewusst
 * ohne React und ohne Fetch, damit der node-Runner sie direkt prüft.
 *
 * Ein Block ist eine Notiz-Zeile. Die Ableitung hier liefert die Blöcke
 * ÄLTESTE ZUERST — das ist die Reihenfolge des Modells, nicht zwingend die
 * der Anzeige: die Fläche zeigt sie so im Kundentagebuch; im
 * Vorgang V4 spiegelt sie den Strom und stellt den jüngsten Block nach oben
 * (`NotizFlaeche.neuesteOben`, PO 14.09.2026). `sortIndex 0` heißt darum in
 * jeder Ansicht „der älteste Platz", und `mischeBloecke` darf sich auf die
 * aufsteigende Folge verlassen.
 *
 * Was in der Eingabezeile entsteht, wird IMMER ein eigener Block
 * (PO 09.09.2026). Bis dahin wuchs der letzte Block weiter, solange er
 * demselben Autor gehörte und die Pause unter fünf Minuten lag —
 * `brauchtNeuenBlock` und `BLOCK_PAUSE_MS` sind mit dieser Regel entfallen.
 */

/**
 * Woher eine Notiz stammt. `"kontakt"` ist die Tagebuch-Notiz — sie kommt nur
 * im Kundentagebuch vor, wo alle drei Tabellen in EINER Fläche zusammenlaufen.
 */
export type NotizQuelle = "vorgang" | "todo" | "kontakt";

/**
 * Eine Notiz, aus der ein Block werden kann. Der Vorgang liefert
 * {@link AggregatedNoteDto}; das Kundentagebuch dieselbe Form mit der dritten
 * Quelle — deshalb steht `source` hier weiter gefasst als dort.
 */
export type BlockNotiz = Omit<AggregatedNoteDto, "source"> & { source?: NotizQuelle };

/** Ein Block der Notizfläche. */
export interface Block {
    /** Stabiler React-Schlüssel über alle Herkunftstabellen. */
    key: string;
    id: number;
    source: NotizQuelle;
    /** Inhalt — HTML, wenn `istHtml`, sonst Klartext. */
    html: string;
    istHtml: boolean;
    /** Ersteller; entscheidet über das Bearbeitungsrecht. */
    autor: string;
    /** Alle, die geschrieben haben — der Blockkopf nennt sie. */
    bearbeiter: string[];
    /**
     * Die per `@` Erwähnten. Wird beim Speichern mitgeschickt, weil das Backend
     * die Liste ERSETZT: ohne den Bestand verlöre der Block bei jeder Änderung
     * seine bisherigen Erwähnungen.
     */
    mentions: string[];
    angelegt: Date;
    /** Letzte Bearbeitung; ohne Bearbeitung = Anlagezeitpunkt. */
    zuletzt: Date;
    refs: NoteRefDto[];
    sortIndex: number | null;
    /** Gezogene Breite in Pixel; null = mit dem Inhalt wachsen. */
    breite: number | null;
    parentId: number | null;
    /**
     * Spiegelt das reMarkable (`quelle = 'remarkable'`): im Feld nicht
     * bearbeitbar, nur kommentier- und löschbar. Fehlt = gewöhnlicher Block.
     */
    gesperrt?: true;
}

/**
 * Ist ein Blockinhalt leer? Ein `contentEditable` hinterlässt beim Leerräumen
 * je nach Browser `<br>` oder `<div><br></div>`, eingefügter Text zusätzlich
 * `&nbsp;` — auf die Zeichenlänge zu schauen ginge an all dem vorbei.
 *
 * Sie steht HIER und nicht in `blockHtml.ts`, weil jenes Modul DOMPurify
 * mitbringt und damit ein DOM braucht; geprüft wird auch in reinen
 * Ableitungen, die im node-Runner laufen (`bloeckeAus`, `archivZeilen`).
 * `istLeer` in `blockHtml.ts` ruft dieselbe Funktion auf — es gibt genau
 * EINE Regel dafür, was leer heißt.
 *
 * Ein BILD ist Inhalt (PO 14.09.2026: „bilder in die notiz laden"). Ein Block
 * nur mit `<img data-bild-id>` hat keinen Text — die Tag-Streichung allein
 * hielte ihn für leer, `bloeckeAus` ließe ihn aus dem Strom fallen und
 * `NotizBlock` löschte ihn beim Verlassen. Spiegel: `istLeererNotiztext`
 * (`apps/api/src/vorgang/note-blocks.ts`).
 */
export function istLeererBlocktext(html: string | null | undefined): boolean {
    const text = html ?? "";
    if (/<img\b/i.test(text)) return false;
    return (
        text
            .replace(/<br\s*\/?>/gi, "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim().length === 0
    );
}

/**
 * Der stabile Schlüssel einer Notiz — „quelle:id".
 *
 * Er MUSS die Quelle tragen: die Liste aus `listAggregated` mischt VorgangNote
 * und TodoNote, zwei Tabellen mit eigenen Id-Folgen — es gibt `vorgang:5` und
 * `todo:5` nebeneinander. Dieselbe Regel gilt in `archivZeilen.ausNotiz` (dort
 * steht der ausführliche Warntext) und beim Ableiten der Kommentare
 * (`kommentarModell.ts`), die sonst am falschen Block hingen.
 */
export function notizSchluessel(n: BlockNotiz): string {
    return `${n.source ?? "vorgang"}:${n.id}`;
}

const zeit = (iso?: string | null): Date => {
    if (!iso) return new Date(0);
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

/**
 * Die Referenzen einer Notiz. Das Backend vereinigt `refs` und die alten
 * Skalar-Bezüge bereits; für Antworten des schlanken Endpoints (der `refs`
 * nicht liefert) wird hier dasselbe noch einmal getan — sonst stünde ein
 * bestehender Kommentar ohne den Eintrag da, auf den er sich bezieht.
 */
function refsAus(n: BlockNotiz): NoteRefDto[] {
    if (n.refs?.length) return n.refs;
    const out: NoteRefDto[] = [];
    if (n.mailLinkId != null) out.push({ art: "mail", id: n.mailLinkId, label: null });
    if (n.callLogId != null) out.push({ art: "anruf", id: n.callLogId, label: null });
    if (n.documentId != null) out.push({ art: "dokument", id: n.documentId, label: null });
    if (n.riskObjectId != null) out.push({ art: "risiko", id: n.riskObjectId, label: n.riskLabel ?? null });
    if (n.contractId != null) out.push({ art: "vertrag", id: n.contractId, label: n.contractLabel ?? null });
    return out;
}

/**
 * Verweise vereinigen — Duplikate über `art:id` fallen weg, die erste Nennung
 * gewinnt (sie trägt in der Regel das gepflegtere Label).
 *
 * Gebraucht beim Einsetzen eines `#`-Bezugs: das Backend ERSETZT `refs`, statt
 * zu ergänzen. Wer nur die neu gewählten schickte, löschte die alten.
 */
export function vereinigeRefs(...listen: Array<NoteRefDto[] | undefined>): NoteRefDto[] {
    const out: NoteRefDto[] = [];
    const gesehen = new Set<string>();
    for (const liste of listen) {
        for (const r of liste ?? []) {
            const schluessel = `${r.art}:${r.id}`;
            if (gesehen.has(schluessel)) continue;
            gesehen.add(schluessel);
            out.push(r);
        }
    }
    return out;
}

/**
 * Die Blöcke EINES Arbeitsstrangs, älteste zuerst.
 *
 * `todoId === null` heißt „der Vorgang selbst" (nur möglich, solange er keine
 * To-dos hat); sonst die Notizen genau dieses To-dos. Antworten (`parentId`)
 * bleiben eigene Blöcke — sie sind Beiträge wie jeder andere und würden sonst
 * beim Umstieg auf die Fläche verschwinden.
 *
 * INHALTSLEERE Notizen fallen raus (PO 13.09.2026: „leergeräumt heißt
 * gelöscht"). Das wirkt ÜBERALL, wo die Fläche steht — Vorgang und
 * Kundentagebuch —, und deshalb muss der Riegel dahinter überall halten: er
 * steht in `VorgangNoteService.update`, `TodoNoteService.update` UND
 * `ContactNoteService.update`. Fehlte er in einer der drei Tabellen, bliebe
 * dort eine Zeile zurück, die diese Filterung unerreichbar macht — nicht
 * sichtbar, nicht beschreibbar, nicht löschbar.
 *
 * Neue leere Notizen entstehen seit demselben Tag nicht mehr. Was vorher
 * entstand, liegt aber noch in der Datenbank und erschiene sonst als Block
 * ohne Text, den man nur am Kopf erkennt; gezählt wird es nicht mehr mit
 * (`archivZeilen`, `sichtbareNotizen`).
 */
export function bloeckeAus(notizen: BlockNotiz[], todoId: number | null): Block[] {
    const out: Block[] = [];
    for (const n of notizen) {
        if ((n.todoId ?? null) !== todoId) continue;
        if (istLeererBlocktext(n.text)) continue;
        const angelegt = zeit(n.timestamp);
        out.push({
            key: notizSchluessel(n),
            id: n.id,
            source: n.source ?? "vorgang",
            html: n.text ?? "",
            istHtml: n.format === "html",
            autor: n.author,
            bearbeiter: n.editors?.length ? n.editors : [n.author],
            mentions: n.mentions ?? [],
            angelegt,
            zuletzt: n.updatedAt ? zeit(n.updatedAt) : angelegt,
            refs: refsAus(n),
            sortIndex: typeof n.sortIndex === "number" ? n.sortIndex : null,
            breite: typeof n.breite === "number" ? n.breite : null,
            parentId: n.parentId ?? null,
            ...(n.quelle === "remarkable" ? { gesperrt: true as const } : {}),
        });
    }
    // Von Hand Verschobenes zuerst und in seiner Reihenfolge; alles andere
    // danach nach Zeit. Gleichstand über die Id, damit nichts bei jedem
    // Rendern tanzt.
    return out.sort((a, b) => {
        const sa = a.sortIndex ?? Number.POSITIVE_INFINITY;
        const sb = b.sortIndex ?? Number.POSITIVE_INFINITY;
        if (sa !== sb) return sa - sb;
        const ta = a.angelegt.getTime();
        const tb = b.angelegt.getTime();
        return ta - tb || a.id - b.id;
    });
}

/**
 * Ein Eintrag des Reihenfolge-Patches: welche Notiz auf welchen Platz.
 *
 * Die QUELLE reist mit (14.09.2026): Der Patch nennt alle Blöcke des Strangs,
 * und im Vorgang können darunter `TodoNote`s stehen — eine andere Tabelle mit
 * eigener Id-Folge. Ein Patch, der
 * nur `id` trüge, ließe die Seite nicht mehr erkennen, an welchen Dienst die
 * Zeile gehört; `PUT vorgang-notes/:id` träfe dann die gleichnamige Notiz der
 * falschen Tabelle — womöglich in einem anderen Vorgang.
 */
export type ReihenfolgePatch = { id: number; source: NotizQuelle; sortIndex: number };

/**
 * Einen Block eine Position nach oben (-1) oder unten (1) schieben — „oben"
 * und „unten" in der ÄLTEST-ZUERST-Liste, die diese Funktion bekommt. Eine
 * Fläche, die den Strom gespiegelt zeigt (V4, `neuesteOben`), dreht die
 * Richtung vor dem Aufruf um; hier bleibt -1 = kleinerer Index.
 *
 * Liefert die NEUEN `sortIndex`-Werte aller Blöcke, nicht nur der beiden
 * getauschten: Wer nur zwei Werte schriebe, hinterließe eine Liste, in der
 * verschobene und unverschobene Blöcke gemischt sind — beim nächsten Laden
 * stünde die Reihenfolge anders da. Am Rand passiert nichts (leerer Patch =
 * kein Request).
 *
 * Geschrieben wird die vollständige Liste atomar; `source` wählt dabei die
 * Tabelle, ohne gleichnamige IDs zu verwechseln.
 */
export function verschiebeBlock(bloecke: Block[], key: string, richtung: -1 | 1): ReihenfolgePatch[] {
    const idx = bloecke.findIndex((b) => b.key === key);
    if (idx < 0) return [];
    const ziel = idx + richtung;
    if (ziel < 0 || ziel >= bloecke.length) return [];
    const next = bloecke.slice();
    const [raus] = next.splice(idx, 1);
    next.splice(ziel, 0, raus);
    return next.map((b, i) => ({ id: b.id, source: b.source, sortIndex: i }));
}

/**
 * Ergänzt den sichtbaren Move-Patch um aktive, in V4 hinter Fäden versteckte
 * Kommentare und leere Altzeilen. Der Server verlangt bewusst die komplette
 * ID-Menge des Strangs; die sichtbaren Blöcke bleiben vorne in der gewählten
 * Ordnung, die nicht bewegten Zeilen folgen stabil.
 */
export function vervollstaendigeReihenfolge(
    patch: ReihenfolgePatch[],
    alleNotizen: BlockNotiz[],
    todoId: number | null,
): ReihenfolgePatch[] {
    const vorhanden = new Set(patch.map((e) => `${e.source}:${e.id}`));
    const rest = alleNotizen
        .filter(
            (n) =>
                (n.source === "vorgang" || n.source === "todo") &&
                (n.todoId ?? null) === todoId &&
                !vorhanden.has(`${n.source}:${n.id}`),
        )
        .sort((a, b) => {
            const ai = typeof a.sortIndex === "number" ? a.sortIndex : Number.POSITIVE_INFINITY;
            const bi = typeof b.sortIndex === "number" ? b.sortIndex : Number.POSITIVE_INFINITY;
            if (ai !== bi) return ai - bi;
            const at = zeit(a.timestamp).getTime();
            const bt = zeit(b.timestamp).getTime();
            if (at !== bt) return at - bt;
            const as = a.source ?? "vorgang";
            const bs = b.source ?? "vorgang";
            if (as !== bs) return as === "vorgang" ? -1 : 1;
            return a.id - b.id;
        });
    return [
        ...patch.map((eintrag, sortIndex) => ({ ...eintrag, sortIndex })),
        ...rest.map((n, index) => ({
            source: n.source as "vorgang" | "todo",
            id: n.id,
            sortIndex: patch.length + index,
        })),
    ];
}

/**
 * Ein FREMDER Block der Fläche — etwas, das keine Notiz ist und trotzdem im
 * Strom steht. In der Vorgangsansicht V4 sind das Mails, Anrufe und Dokumente
 * (Zeichnung 10.09.2026: „die Mails, Doks, Anrufe sind im Notizbereich").
 * Die Fläche weiß über sie nur zweierlei: wann, und unter welchem Schlüssel.
 */
export type FremdBlock = { key: string; zeit: Date };

/**
 * Fremde Blöcke zwischen die Notizblöcke schieben.
 *
 * Die Notizen behalten dabei IHRE Reihenfolge — die aus {@link bloeckeAus},
 * inklusive dessen, was jemand von Hand verschoben hat. Nur die fremden
 * Blöcke werden einsortiert, und zwar vor den ersten Notizblock, der jünger
 * ist als sie.
 *
 * Nicht einfach alles nach Zeit zu sortieren ist Absicht: `sortIndex` wäre
 * dann wirkungslos, und ein Block, den jemand bewusst nach oben gezogen hat,
 * spränge beim nächsten Laden zurück.
 */
export function mischeBloecke<F extends FremdBlock>(bloecke: Block[], fremde: F[]): Array<Block | F> {
    if (!fremde.length) return bloecke;
    const sortiert = [...fremde].sort(
        (a, b) => a.zeit.getTime() - b.zeit.getTime() || a.key.localeCompare(b.key),
    );
    const out: Array<Block | F> = [];
    let i = 0;
    for (const b of bloecke) {
        while (i < sortiert.length && sortiert[i].zeit.getTime() <= b.angelegt.getTime()) {
            out.push(sortiert[i]);
            i += 1;
        }
        out.push(b);
    }
    while (i < sortiert.length) {
        out.push(sortiert[i]);
        i += 1;
    }
    return out;
}

/** Trennt Notiz von Fremdblock — nur der Fremdblock trägt `zeit`. */
export function istFremd<F extends FremdBlock>(e: Block | F): e is F {
    return (e as F).zeit instanceof Date;
}
